import { ReactNode, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../../stores/appStore';
import Header from './Header';
import Footer from './Footer';
import EventModal from '../Events/EventModal';
import ChatBot from '../Chat/ChatBot';
import OfflineBanner from './OfflineBanner';
import PullToRefresh from './PullToRefresh';
import MobilePullToRefresh from './MobilePullToRefresh';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { isOffline, isEventModalOpen, theme, settings, isLoading } = useAppStore();
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if AI Assistant is enabled - only show when settings are loaded and enabled
  const hasSettingsLoaded = Object.keys(settings).length > 0;
  const isAIAssistantEnabled = hasSettingsLoaded ? (settings?.['features.ai_assistant'] === true) : false;

  useEffect(() => {
    // Detect if mobile device
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className={`layout theme-${theme} min-h-screen bg-gray-50 flex flex-col`}>
      {/* Offline Banner */}
      <AnimatePresence>
        {isOffline && <OfflineBanner />}
      </AnimatePresence>

      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-1 relative">
        {isMobile ? (
          <MobilePullToRefresh>
            <motion.div
              className="min-h-full pb-20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {children}
            </motion.div>
          </MobilePullToRefresh>
        ) : (
          <motion.div
            className="min-h-full pb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <Footer />

      {/* Modals & Overlays */}
      <AnimatePresence>
        {isEventModalOpen && <EventModal />}
      </AnimatePresence>

      {/* Chat Bot - Only show if AI Assistant is enabled */}
      {isAIAssistantEnabled && <ChatBot />}

      {/* PWA Install Prompt */}
      <div id="pwa-install-prompt" />
    </div>
  );
};

export default Layout;